import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AppShell } from '@/components/app/AppShell';
import { CitedMarkdown } from '@/components/ui/CitedMarkdown';
import { InlineAlert } from '@/components/app/Modal';
import { EmptyState } from '@/components/app/EmptyState';
import { CitationChip } from '@/components/app/CitationChip';
import { RequestSignoffButton } from '@/components/app/RequestSignoffButton';
import { useDialogs } from '@/components/app/DialogProvider';
import { ApprovalKind } from '@/lib/approvals';
import { aiResultsApi, AiResult, AiResultKind } from '@/lib/ai-results';
import { modelTierLabel } from '@/lib/model-label';
import { useFormatDateTime } from '@/lib/datetime';

// Map a saved result back to the agent that produced it.
const KIND_ROUTE: Record<string, string> = {
  search: 'ask',
  compose: 'compose',
  brief: 'brief',
  compare: 'compare-docs',
  audit: 'audit',
  workflow: 'workflows',
  research: 'research',
};

// AI result kinds that map to an approvable target (others have no sign-off).
const SIGNOFF_KIND: Record<string, ApprovalKind | undefined> = {
  compose: 'compose_output',
  brief: 'brief',
};

const KINDS: (AiResultKind | 'all')[] = ['all', 'search', 'compose', 'brief', 'compare', 'audit', 'workflow'];

/**
 * The workspace's saved-AI-results library. Closes the "I see the answer but
 * can't keep or reuse it" gap: every agent page now has a Save button that
 * lands a row here, where it can be reopened, pinned, or deleted.
 */
export function AiResultsPage() {
  const { t } = useTranslation();
  const { wsId } = useParams<{ wsId: string }>();
  const qc = useQueryClient();
  const { confirm } = useDialogs();
  const [filter, setFilter] = useState<AiResultKind | 'all'>('all');
  const [openId, setOpenId] = useState<string | null>(null);
  const fmt = useFormatDateTime();

  const list = useQuery({
    queryKey: ['ai-results', wsId, filter],
    queryFn: () => aiResultsApi.list(wsId!, filter === 'all' ? undefined : filter),
    enabled: !!wsId,
  });

  const pin = useMutation({
    mutationFn: (r: AiResult) => aiResultsApi.pin(wsId!, r.id, !r.pinned),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ai-results', wsId] }),
  });
  const remove = useMutation({
    mutationFn: (id: string) => aiResultsApi.remove(wsId!, id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ai-results', wsId] }),
  });

  const confirmDelete = async (r: AiResult) => {
    const ok = await confirm({
      title: t('ai_results.delete_confirm', { defaultValue: 'Delete this saved result?' }) as string,
      confirmLabel: t('common.delete', { defaultValue: 'Delete' }) as string,
    });
    if (ok) {
      remove.mutate(r.id);
      if (openId === r.id) setOpenId(null);
    }
  };

  return (
    <AppShell
      title={t('ai_results.title', { defaultValue: 'AI Results' })}
      description={t('ai_results.subtitle', { defaultValue: 'Saved answers from Ask, Compose, Brief, Compare, and Audit — reopen, pin, or remove.' }) as string}
      maxWidth="max-w-4xl"
    >
      <div className="space-y-4">
        <div className="flex flex-wrap gap-2">
          {KINDS.map((k) => (
            <button
              key={k}
              onClick={() => setFilter(k)}
              className={`px-3 py-1.5 text-xs rounded-full border transition ${
                filter === k ? 'bg-accent-600 text-white border-accent-600' : 'border-ink-200 text-ink-600 hover:bg-ink-50'
              }`}
            >
              {t(`ai_results.kind.${k}`, { defaultValue: k })}
            </button>
          ))}
        </div>

        {list.isLoading && <p className="text-sm text-ink-500">{t('common.loading', { defaultValue: 'Loading…' })}</p>}
        {list.data && list.data.length === 0 && (
          <EmptyState
            title={t('ai_results.empty_title', { defaultValue: 'No saved results yet' }) as string}
            description={t('ai_results.empty_sub', { defaultValue: 'Run an agent and click “Save result” — Ask, Compose, Brief, Compare and Audit all land here.' }) as string}
            actions={[
              { label: t('nav.ask', { defaultValue: 'Ask' }) as string, to: `/app/workspace/${wsId}/ask`, primary: true },
              { label: t('compose.title', { defaultValue: 'Compose' }) as string, to: `/app/workspace/${wsId}/compose` },
              { label: t('brief_agent.title', { defaultValue: 'Brief' }) as string, to: `/app/workspace/${wsId}/brief` },
            ]}
          />
        )}

        {(() => {
          const rows = list.data ?? [];
          const pinnedRows = rows.filter((r) => r.pinned);
          const otherRows = rows.filter((r) => !r.pinned);
          const showGroups = pinnedRows.length > 0 && otherRows.length > 0;

          const renderCard = (r: AiResult) => (
            <section
              key={r.id}
              className={`card-paper p-4 ${r.pinned ? 'ring-1 ring-citation-300/70 bg-citation-50/30' : ''}`}
            >
              <div className="flex items-start justify-between gap-3">
                <button className="text-left flex-1 min-w-0" onClick={() => setOpenId(openId === r.id ? null : r.id)}>
                  <div className="flex items-center gap-2">
                    {r.pinned && <span className="text-citation" title={t('ai_results.pinned_title', { defaultValue: 'Pinned to top' })}>★</span>}
                    <span className="chip-citation text-[10px] uppercase">{t(`ai_results.kind.${r.kind}`, { defaultValue: r.kind })}</span>
                    <h3 className="font-medium text-ink-900 truncate">{r.title}</h3>
                  </div>
                  <div className="mt-1 text-xs text-ink-500">
                    {fmt(r.createdAt)}
                    {r.model ? ` · ${modelTierLabel(r.model)}` : ''}
                    {r.costCents != null ? ` · ¢${r.costCents}` : ''}
                    {r.citedChunkIds.length ? ` · ${r.citedChunkIds.length} ${t('ask.citations', { defaultValue: 'citations' })}` : ''}
                  </div>
                </button>
                <div className="flex items-center gap-1 shrink-0">
                  <button onClick={() => pin.mutate(r)} className="px-2 py-1 text-xs text-ink-500 hover:text-citation" title={r.pinned ? 'Unpin — stop keeping this at the top' : 'Pin — keep this at the top of the list'}>
                    {r.pinned ? t('ai_results.unpin', { defaultValue: 'Unpin' }) : t('ai_results.pin', { defaultValue: 'Pin' })}
                  </button>
                  <button onClick={() => confirmDelete(r)} className="px-2 py-1 text-xs text-rose-600 hover:text-rose-700" title={t('common.delete', { defaultValue: 'Delete' })}>
                    {t('common.delete', { defaultValue: 'Delete' })}
                  </button>
                </div>
              </div>
              {openId === r.id && (
                <div className="mt-3 pt-3 border-t border-ink-100">
                  <CitedMarkdown content={r.output} wsId={wsId} />
                  {r.citedChunkIds.length > 0 && (
                    <div className="mt-3 flex flex-wrap items-center gap-1.5">
                      <span className="text-[11px] uppercase tracking-wide font-semibold text-ink-500">
                        {t('ai_results.sources', { defaultValue: 'Sources' })}
                      </span>
                      {r.citedChunkIds.map((id) => (
                        <CitationChip key={id} wsId={wsId!} chunkId={id} />
                      ))}
                    </div>
                  )}
                  <div className="mt-3 flex items-center gap-4">
                    <Link
                      to={
                        r.kind === 'search'
                          ? `/app/workspace/${wsId}/ask?seed=${encodeURIComponent(r.title)}`
                          : `/app/workspace/${wsId}/${KIND_ROUTE[r.kind] ?? 'ask'}`
                      }
                      className="text-[12px] text-accent-600 hover:underline"
                    >
                      {t('ai_results.rerun', { defaultValue: 'Re-run in {{agent}} →', agent: t(`ai_results.kind.${r.kind}`, { defaultValue: r.kind }) })}
                    </Link>
                    {SIGNOFF_KIND[r.kind] && (
                      <RequestSignoffButton wsId={wsId!} targetKind={SIGNOFF_KIND[r.kind]!} targetId={r.id} />
                    )}
                  </div>
                </div>
              )}
            </section>
          );

          const groupHeader = (label: string) => (
            <div className="text-[10.5px] uppercase tracking-[0.16em] text-ink-400 font-semibold pt-1">
              {label}
            </div>
          );

          return (
            <div className="space-y-3">
              {showGroups && groupHeader(t('ai_results.group_pinned', { defaultValue: 'Pinned' }) as string)}
              {pinnedRows.map(renderCard)}
              {showGroups && groupHeader(t('ai_results.group_others', { defaultValue: 'All results' }) as string)}
              {otherRows.map(renderCard)}
            </div>
          );
        })()}
      </div>
    </AppShell>
  );
}
